import { HttpResponse } from '@angular/common/http';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback';
import { ProgressIndicatorService } from '@services/progress';
import { finalize } from 'rxjs/operators';

import { I18nService } from '../../../../../../i18n/services';
import { DialogClass, DialogService } from '../../../../../../services/dialog';
import { IAppTermDocument, LegalsService, TermState, TermType } from '../../../../../../services/legals';
import { IUserTerms, UsersService } from '../../../../../../services/users';
import { UsersTermsAcceptDialogComponent } from '../users-terms-accept-dialog';
import { ITermsAcceptanceDialogData } from './users-terms-acceptance.models';

@Component({
	selector: 'app-users-terms-acceptance',
	templateUrl: './users-terms-acceptance.dialog.html'
})
export class UsersTermsAcceptanceDialogComponent implements AfterViewInit {
	@ViewChild(MatSort) sort: MatSort;
	@ViewChild(MatTable) table: MatTable<any>;

	public formInputs = this.formBuilder.group({});

	public loaded = false;
	public acceptedTerms: IUserTerms[] = [];
	public acceptedTermsPaginated: IUserTerms[] = [];
	public tableColumns: string[] = ['displayName', 'version', 'acceptedBy', 'acceptedOn', 'action'];
	public openedMenu: number;
	public pageSize = 10;
	public pageIndex = 0;
	public object = Object;

	constructor(
		private feedbackService: FeedbackService,
		public usersService: UsersService,
		private progressIndicatorService: ProgressIndicatorService,
		@Inject(MAT_DIALOG_DATA) public dialogData: ITermsAcceptanceDialogData,
		private dialogRef: MatDialogRef<UsersTermsAcceptanceDialogComponent>,
		private formBuilder: FormBuilder,
		private legalsService: LegalsService,
		private i18nService: I18nService,
		private dialogService: DialogService
	) {}

	public ngAfterViewInit(): void {
		this.sort.sortChange.subscribe((value: { active: string; direction: string }) => {
			this.paginateResults({ pageIndex: 0, pageSize: this.pageSize }, value.active, value.direction);
		});

		this.getTerms();
	}

	public paginateResults(
		event: PageEvent | { pageIndex: number; pageSize: number; length?: number } = { pageIndex: 0, pageSize: 10 },
		active = this.sort.active,
		direction = this.sort.direction.toString()
	): void {
		this.pageIndex = event.pageIndex;
		this.pageSize = event.pageSize;

		this.acceptedTermsPaginated = this.acceptedTerms
			.sort((a, b) => {
				const aValue =
					active === 'acceptedOn'
						? this.formInputs.controls[a.termsId + 'Version'].value.date + ''
						: active === 'acceptedBy'
						? this.formInputs.controls[a.termsId + 'Version'].value.clientId
						: a[active];
				const bValue =
					active === 'acceptedOn'
						? this.formInputs.controls[b.termsId + 'Version'].value.date + ''
						: active === 'acceptedBy'
						? this.formInputs.controls[b.termsId + 'Version'].value.clientId
						: b[active];

				return direction === 'asc'
					? bValue.localeCompare(aValue)
					: direction === 'desc'
					? aValue.localeCompare(bValue)
					: a.displayName.localeCompare(b.displayName);
			})
			.slice(this.pageIndex * this.pageSize, this.pageIndex * this.pageSize + this.pageSize);
	}

	public close(): void {
		this.dialogRef.close();
	}

	public viewTerm(userTerm: IUserTerms, version: number): void {
		if (userTerm.versions[version]?.url) {
			this.legalsService.serveExternalDocument(userTerm.versions[version].url);
		} else {
			this.progressIndicatorService.spinner(true);

			const term: IAppTermDocument = {
				companyId: userTerm.companyId,
				termsId: userTerm.termsId,
				entityType: 'oauthapp',
				displayName: userTerm.displayName,
				active: true,
				version: version,
				displayVersion: userTerm.versions[version].displayVersion,
				status: 'active'
			};

			this.legalsService
				.getTermData(userTerm.termsId, version, TermType.app)
				.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
				.subscribe((res: HttpResponse<string>) => {
					this.legalsService.serveDocument(
						term,
						res,
						false,
						TermType.app,
						TermState.live,
						null
					);
				});
		}
	}

	public acceptTerm(): void {
		this.dialogService.open(UsersTermsAcceptDialogComponent, {
			data: {
				user: this.dialogData.user,
				clusterId: this.dialogData.clusterId,
				callback: (accepted: boolean) => {
					if (accepted) this.getTerms(true);
				}
			}
		});
	}

	public confirmRevokeTermAcceptance(termsId: string): void {
		this.feedbackService.confirm({
			title: FeedbackTitle.warning,
			message: this.i18nService.get(
				'Revoking acceptance of terms will force users to re-accept any terms and conditions before they can continue. Are you sure you want to continue?',
				'app.prompt.users.confirm-accept-user-terms'
			),
			style: FeedbackStyle.error,
			callback: (confirmed: boolean) => {
				if (confirmed) this.revokeTermAcceptance(termsId);
			}
		});
	}

	private getTerms(force = false): void {
		this.progressIndicatorService.spinner(true);

		this.usersService.getAcceptedTerms(this.dialogData.user.identities['COGNITO'], this.dialogData.clusterId, force).subscribe(
			(res: IUserTerms[]) => {
				this.acceptedTerms = res;
				this.createVersionFormControls();

				setTimeout(() => {
					this.paginateResults();
					this.loaded = true;
				});
			},
			(error: any) => {
				this.dialogRef.close();
				this.feedbackService.apiError(error);
			},
			() => this.progressIndicatorService.spinner(false)
		);
	}

	private revokeTermAcceptance(termsId: string): void {
		this.progressIndicatorService.spinner(true);

		this.usersService.revokeTermAcceptance(this.dialogData.user.identities['COGNITO'], this.dialogData.clusterId, termsId).subscribe(
			() => {
				this.acceptedTerms = this.usersService.data.terms[this.dialogData.clusterId][this.dialogData.user.identities['COGNITO']]
					? this.usersService.data.terms[this.dialogData.clusterId][this.dialogData.user.identities['COGNITO']]
					: [];

				this.createVersionFormControls();
				this.paginateResults();
			},
			(error: any) => this.feedbackService.apiError(error),
			() => this.progressIndicatorService.spinner(false)
		);
	}

	private createVersionFormControls(): void {
		Object.keys(this.formInputs.controls).forEach((key) => {
			this.formInputs.removeControl(key);
		});

		this.acceptedTerms.forEach((term) => {
			const version = Object.keys(term.versions).sort().reverse()[0];
			this.formInputs.addControl(`${term.termsId}Version`, this.formBuilder.control(term.versions[version]));
		});
	}
}
